// src/pages/Platform/EmailTemplates.js
//
// The Super Admin's editor for every transactional email this codebase
// actually sends (see email_template_handler.go / seed_email_templates.go)
// — structurally a near-copy of MarketingContent.js's own language-tab
// editor (fetch once, per-language `draft` state, one "Save" button per
// active language tab), but each entry is a SUBJECT+BODY pair instead of one
// plain field, and there's no "add a new template" affordance anywhere —
// this is a FIXED set of three keys, one per real send site in the code
// (models.EmailTemplate's own doc comment), not an open-ended list the way
// MarketingContent.js's business types/FAQ sections are. English itself is
// fixed in code — shown read-only as reference next to every field, never
// editable here — same convention MarketingContent.js already follows.
//
// Body is now full HTML (seed_email_templates.go's own doc comment on why —
// a table-based, inline-styled design, no <style>/<link> block), not plain
// text, so both the English reference body and the active language's
// editable body get: a monospace, taller textarea/box (raw markup is much
// easier to scan in a fixed-width font than the proportional font used for
// everything else on this page), and a live rendered preview underneath via
// <iframe srcDoc={...}> — an iframe's srcDoc renders in its own isolated
// document, so the email's own inline styles never leak out and affect the
// rest of this admin page, and the reverse (this page's own styles leaking
// into the previewed email) can't happen either. The draft preview re-renders
// on every keystroke since srcDoc is just a prop; no debounce is needed for
// how infrequently this page is actually edited.
import { useState, useEffect, useCallback, useContext } from "react";
import { getEmailTemplates, saveEmailTemplates } from "../../services/api";
import { PlatformAuthContext } from "../../context/PlatformAuthContext";

function buildDraft(templateList, langCode) {
  const next = {};
  for (const t of templateList) {
    const override = (langCode && t.values?.[langCode]) || {};
    next[t.key] = { subject: override.subject || "", body: override.body || "" };
  }
  return next;
}

export default function EmailTemplates() {
  const { token } = useContext(PlatformAuthContext);

  const [languages, setLanguages] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [activeLangCode, setActiveLangCode] = useState(null);
  const [draft, setDraft] = useState({}); // key -> {subject, body} (current language only)
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await getEmailTemplates(token);
      const langs = res.languages || [];
      setLanguages(langs);
      setTemplates(res.templates || []);
      const firstLang = activeLangCode && langs.some((l) => l.code === activeLangCode)
        ? activeLangCode
        : langs[0]?.code || null;
      setActiveLangCode(firstLang);
      setDraft(buildDraft(res.templates || [], firstLang));
    } catch (err) {
      setError(err.message || "Failed to load email templates");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const switchLang = (code) => {
    setActiveLangCode(code);
    setDraft(buildDraft(templates, code));
    setSaved(false);
  };

  const setField = (key, field, value) => {
    setDraft((d) => ({ ...d, [key]: { ...d[key], [field]: value } }));
  };

  const handleSave = async () => {
    if (!activeLangCode) return;
    setSaving(true);
    setError("");
    setSaved(false);
    try {
      const items = Object.entries(draft).map(([key, v]) => ({ key, subject: v.subject, body: v.body }));
      await saveEmailTemplates(token, activeLangCode, items);
      setSaved(true);
      await load();
    } catch (err) {
      setError(err.message || "Failed to save email templates");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={{ padding: 40, textAlign: "center", color: "#94a3b8" }}>Loading...</div>;

  return (
    <div style={{ maxWidth: 900 }}>
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ margin: 0, fontSize: 20, color: "#0f172a" }}>Email templates</h2>
        <p style={{ fontSize: 13, color: "#64748b", marginTop: 6, marginBottom: 0 }}>
          Wording for every automated email this system sends. Each "{"{{token}}"}" placeholder in a
          template's reference text below is replaced with real data when that email actually goes out.
          Body is raw HTML — edit the markup directly; the preview underneath each box shows how it will
          actually render.
        </p>
      </div>

      {languages.length === 0 ? (
        <div style={{ padding: 24, color: "#94a3b8", textAlign: "center", background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0" }}>
          No additional languages yet — add one from the Marketing languages page first. (English is always
          the built-in default.)
        </div>
      ) : (
        <>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 20 }}>
            {languages.map((l) => (
              <button
                key={l.code}
                onClick={() => switchLang(l.code)}
                style={{
                  ...secondaryBtnStyle,
                  background: activeLangCode === l.code ? "#0f172a" : "#fff",
                  color: activeLangCode === l.code ? "#fff" : "#334155",
                }}
              >
                {l.name}
              </button>
            ))}
          </div>

          {templates.map((t) => (
            <section key={t.key} style={sectionStyle}>
              <h3 style={sectionTitleStyle}>{t.name}</h3>
              {t.description && (
                <p style={{ fontSize: 13, color: "#64748b", marginTop: -6, marginBottom: 14 }}>{t.description}</p>
              )}

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 14 }}>
                <div>
                  <div style={fieldLabelStyle}>English subject (reference)</div>
                  <div style={referenceBoxStyle}>{t.english_subject || <em style={{ color: "#cbd5e1" }}>(empty)</em>}</div>
                </div>
                <div>
                  <div style={fieldLabelStyle}>&nbsp;</div>
                  <input
                    style={inputStyle}
                    value={draft[t.key]?.subject ?? ""}
                    onChange={(e) => setField(t.key, "subject", e.target.value)}
                    placeholder={t.english_subject}
                  />
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                <div>
                  <div style={fieldLabelStyle}>English body HTML (reference)</div>
                  <div style={htmlBoxStyle}>
                    {t.english_body || <em style={{ color: "#cbd5e1" }}>(empty)</em>}
                  </div>
                  <div style={fieldLabelStyle}>Preview</div>
                  <iframe
                    title={`${t.key}-english-preview`}
                    srcDoc={t.english_body || "<p></p>"}
                    style={previewFrameStyle}
                  />
                </div>
                <div>
                  <div style={fieldLabelStyle}>&nbsp;</div>
                  <textarea
                    style={htmlTextareaStyle}
                    value={draft[t.key]?.body ?? ""}
                    onChange={(e) => setField(t.key, "body", e.target.value)}
                    placeholder={t.english_body}
                    spellCheck={false}
                  />
                  <div style={fieldLabelStyle}>Preview</div>
                  <iframe
                    title={`${t.key}-draft-preview`}
                    srcDoc={draft[t.key]?.body || t.english_body || "<p></p>"}
                    style={previewFrameStyle}
                  />
                </div>
              </div>
            </section>
          ))}

          {error && <div style={errorBoxStyle}>{error}</div>}
          {saved && <div style={successBoxStyle}>Email templates saved.</div>}

          <button type="button" style={primaryBtnStyle} onClick={handleSave} disabled={saving || !activeLangCode}>
            {saving ? "Saving..." : "Save templates"}
          </button>
        </>
      )}
    </div>
  );
}

const sectionStyle = { background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 18, marginBottom: 16 };
const sectionTitleStyle = { margin: "0 0 14px", fontSize: 14, color: "#0f172a" };
const fieldLabelStyle = { fontSize: 11, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", marginBottom: 4 };
const referenceBoxStyle = { fontSize: 13, color: "#475569", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: "8px 10px", minHeight: 36 };
const inputStyle = { padding: "9px 12px", borderRadius: 8, border: "1px solid #cbd5e1", fontSize: 14, width: "100%", boxSizing: "border-box" };
// htmlBoxStyle/htmlTextareaStyle: the plain referenceBoxStyle/inputStyle pair
// above is still used for the one-line subject fields, but the body is now
// raw HTML markup rather than a sentence or two of plain text — a monospace
// font and a much taller box make the markup itself readable, the same
// reasoning a code editor picks a fixed-width font over the page's own body
// font. previewFrameStyle sizes the <iframe srcDoc={...}> underneath each
// box; the iframe's own document supplies the email's real background/
// padding, so this frame only needs a border to read as "separate surface"
// and a scrollbar for a body taller than the fixed preview height.
const htmlBoxStyle = { ...referenceBoxStyle, whiteSpace: "pre-wrap", minHeight: 160, maxHeight: 260, overflowY: "auto", fontFamily: "'Courier New', ui-monospace, monospace", fontSize: 12, marginBottom: 10 };
const htmlTextareaStyle = { ...inputStyle, minHeight: 160, maxHeight: 260, resize: "vertical", fontFamily: "'Courier New', ui-monospace, monospace", fontSize: 12, marginBottom: 10 };
const previewFrameStyle = { width: "100%", height: 220, border: "1px solid #e2e8f0", borderRadius: 8, background: "#fff" };
const primaryBtnStyle = { background: "#0f172a", color: "#fff", border: "none", borderRadius: 8, padding: "10px 18px", fontSize: 14, fontWeight: 600, cursor: "pointer" };
const secondaryBtnStyle = { background: "#fff", color: "#334155", border: "1px solid #cbd5e1", borderRadius: 8, padding: "9px 14px", fontSize: 14, cursor: "pointer" };
const errorBoxStyle = { background: "#fee2e2", color: "#991b1b", borderRadius: 8, padding: "8px 12px", fontSize: 13, marginBottom: 12 };
const successBoxStyle = { background: "#dcfce7", color: "#166534", borderRadius: 8, padding: "8px 12px", fontSize: 13, marginBottom: 12 };
